import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Shared "Add attendee" / walk-up comp dialog.
 * Used by AdminDashboard → Attendees tab AND Door Mode walk-up button.
 */
export default function AddAttendeeDialog({ open, onOpenChange, eventId, eventTitle, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", phone: "", date_of_birth: "", quantity: 1, note: "", send_confirmation: true });
  const [busy, setBusy] = useState(false);
  const reset = () => setForm({ email: "", name: "", phone: "", date_of_birth: "", quantity: 1, note: "", send_confirmation: true });

  const submit = async () => {
    if (!form.email || !form.name) return toast.error("Name and email are required");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/attendees`, form);
      toast.success(data.user_created ? `${form.email} added and comp'd in` : `${form.email} comp'd in`);
      reset();
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add walk-up</DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">Comp someone into {eventTitle || "this event"}. Creates a free ticket; no Stripe charge.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><div className="text-eyebrow mb-1">Full name</div><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="rounded-xl" data-testid="add-att-name" autoFocus /></div>
          <div><div className="text-eyebrow mb-1">Email</div><Input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className="rounded-xl" data-testid="add-att-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-eyebrow mb-1">Phone <span className="text-neutral-400 text-xs">(optional)</span></div><Input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} className="rounded-xl" data-testid="add-att-phone" /></div>
            <div><div className="text-eyebrow mb-1">DOB <span className="text-neutral-400 text-xs">(optional)</span></div><Input type="date" value={form.date_of_birth} onChange={e=>setForm({...form, date_of_birth: e.target.value})} className="rounded-xl" data-testid="add-att-dob" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-eyebrow mb-1">Quantity</div><Input type="number" min="1" max="10" value={form.quantity} onChange={e=>setForm({...form, quantity: Number(e.target.value)})} className="rounded-xl" data-testid="add-att-qty" /></div>
            <div><div className="text-eyebrow mb-1">Internal note <span className="text-neutral-400 text-xs">(optional)</span></div><Input value={form.note} onChange={e=>setForm({...form, note: e.target.value})} className="rounded-xl" placeholder="VIP, press, host, walk-up…" data-testid="add-att-note" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.send_confirmation} onChange={e=>setForm({...form, send_confirmation: e.target.checked})} className="h-4 w-4 accent-black" data-testid="add-att-send-email" />
            Send them a branded ticket confirmation email
          </label>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-black text-white btn-pill" data-testid="add-att-submit">{busy ? "Adding…" : "Add walk-up"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
